/* eslint-disable security/detect-object-injection */
import { getCookieValue } from '../cookies'
import { Config, ServerContext } from '../../types/interfaces'

export type GetDomainLocaleResult = {
  isSupported: boolean
  supportedLanguages?: string[]
  country?: string
  language?: string
  locale?: string
  /** Set when host is `{storeKey}.{apex}` — GSSP redirects before template fetch. */
  partnerRedirectPath?: string
}

export type DomainConfigData = {
  domains: Record<string, string>
  config: Config
  version: string
}

type GetDomainLocale = (
  context: ServerContext,
  configData: DomainConfigData
) => GetDomainLocaleResult

type CacheEntry = {
  result: GetDomainLocaleResult
  cacheTime: number
  configVersion: string
}

const domainResolutionCache = new Map<string, CacheEntry>()

const RESOLUTION_CACHE_TTL = 30 * 60 * 1000 // 30 minutes
const MAX_CACHE_ENTRIES = 500

type HostResolution = {
  country: string
  subdomainLabel: string | null
}

/**
 * Map request host to country apex and optional partner subdomain label.
 */
function resolveHostToCountry(
  host: string,
  domains: Record<string, string>
): HostResolution | null {
  const normalized = host.toLowerCase()
  if (!normalized) return null

  const directCountry = domains[normalized]
  if (directCountry) {
    return { country: directCountry, subdomainLabel: null }
  }

  for (const apexHost of Object.keys(domains)) {
    const suffix = `.${apexHost}`
    if (!normalized.endsWith(suffix)) continue

    const label = normalized.slice(0, normalized.length - suffix.length)
    if (!label || label.includes('.')) return null

    const country = domains[apexHost]
    if (!country) return null

    return { country, subdomainLabel: label }
  }

  return null
}

function resolvePartnerRedirectPath(
  country: string,
  subdomainLabel: string | null,
  config: Config
): string | undefined {
  if (!subdomainLabel) return undefined

  const storeIds = config[country]?.storeIds
  if (!storeIds) return '/'

  const labelLower = subdomainLabel.toLowerCase()
  for (const [storeId, storeKey] of Object.entries(storeIds)) {
    if (storeKey.toLowerCase() === labelLower) {
      return `/store/${storeId}`
    }
  }

  return '/'
}

/**
 * Clear resolution cache (called when configuration changes)
 */
export function clearResolutionCache(): void {
  domainResolutionCache.clear()
}

/**
 * Evict expired entries and enforce max size to prevent unbounded growth
 */
function pruneCache(): void {
  if (domainResolutionCache.size <= MAX_CACHE_ENTRIES) return

  const now = Date.now()

  for (const [key, entry] of domainResolutionCache) {
    if (now - entry.cacheTime >= RESOLUTION_CACHE_TTL) {
      domainResolutionCache.delete(key)
    }
  }

  // If still over limit after TTL eviction, drop oldest entries
  if (domainResolutionCache.size > MAX_CACHE_ENTRIES) {
    const excess = domainResolutionCache.size - MAX_CACHE_ENTRIES
    const keys = domainResolutionCache.keys()
    for (let i = 0; i < excess; i++) {
      const { value } = keys.next()
      if (value) domainResolutionCache.delete(value)
    }
  }
}

/**
 * Resolve domain locale with optimized performance
 * Pure function that determines country, language and locale based on domain and request context
 * @param context Server request context
 * @param configData Domain and country configuration data
 * @returns Locale resolution result
 */
const resolveDomainLocale: GetDomainLocale = (context, configData) => {
  // Extract host information efficiently
  const headers = context.req?.headers
  if (!headers) {
    return { isSupported: false }
  }

  // Get host with minimal string operations
  const xForwardedHost = headers['x-forwarded-host']
  const rawHost = xForwardedHost
    ? (typeof xForwardedHost === 'string' ? xForwardedHost : xForwardedHost[0])?.split(
        ','
      )[0]
    : headers.host

  if (!rawHost) {
    return { isSupported: false }
  }

  const host = rawHost.split(':')[0].toLowerCase()

  // Generate cache key that includes configuration version for cache invalidation
  const configVersion = configData.version || 'unknown'
  const cacheKey = `${host}-${configVersion}`

  // Check resolution cache for domain→country mapping (static per config version)
  const now = Date.now()
  const cachedResult = domainResolutionCache.get(cacheKey)

  let country: string | undefined
  let supportedLanguages: string[] | undefined
  let hostResolution: HostResolution | null = null

  if (
    cachedResult &&
    now - cachedResult.cacheTime < RESOLUTION_CACHE_TTL &&
    cachedResult.configVersion === configVersion
  ) {
    if (!cachedResult.result.isSupported) {
      return cachedResult.result
    }
    country = cachedResult.result.country
    supportedLanguages = cachedResult.result.supportedLanguages
  } else {
    hostResolution = resolveHostToCountry(host, configData.domains)

    if (!hostResolution || !configData.config[hostResolution.country]) {
      const result = { isSupported: false }

      domainResolutionCache.set(cacheKey, {
        result,
        cacheTime: now,
        configVersion,
      })

      return result
    }

    country = hostResolution.country

    supportedLanguages = configData.config[country].languages
    if (!supportedLanguages?.length) {
      const result = { isSupported: false }

      domainResolutionCache.set(cacheKey, {
        result,
        cacheTime: now,
        configVersion,
      })

      return result
    }

    // Cache the domain→country resolution (without language, which is per-request)
    domainResolutionCache.set(cacheKey, {
      result: { isSupported: true, supportedLanguages, country },
      cacheTime: now,
      configVersion,
    })

    pruneCache()
  }

  if (!hostResolution) {
    hostResolution = resolveHostToCountry(host, configData.domains)
  }

  const partnerRedirectPath = hostResolution
    ? resolvePartnerRedirectPath(
        country!,
        hostResolution.subdomainLabel,
        configData.config
      )
    : undefined

  // Language is always resolved fresh from the request (depends on cookies/query)
  const language = determineLanguage(context, supportedLanguages!)

  return {
    isSupported: true,
    supportedLanguages,
    country,
    language,
    locale: `${language}_${country!.toUpperCase()}`,
    ...(partnerRedirectPath !== undefined ? { partnerRedirectPath } : {}),
  }
}

/**
 * Determine the best language based on preferences and supported languages
 */
function determineLanguage(
  context: ServerContext,
  supportedLanguages: string[]
): string {
  // Get language preferences with minimal operations
  const queryLanguage = context.query?.language
  const preferredFromQuery = Array.isArray(queryLanguage)
    ? queryLanguage[0]
    : queryLanguage

  if (preferredFromQuery && supportedLanguages.includes(preferredFromQuery)) {
    return preferredFromQuery
  }

  // Only extract language cookie if query language is not available/supported
  try {
    const cookieString = context.req?.headers.cookie || ''
    const cookieLanguage = getCookieValue(cookieString, 'language')

    if (cookieLanguage && supportedLanguages.includes(cookieLanguage)) {
      return cookieLanguage
    }
  } catch (error) {
    // Cookie extraction failed, continue with default
    console.warn('Failed to extract language cookie:', error)
  }

  // Return first supported language as default
  return supportedLanguages[0]
}

/**
 * Get resolution cache statistics for monitoring
 */
export function getResolutionCacheStats(): {
  size: number
  entries: Array<{ cacheKey: string; cacheAge: number; configVersion: string }>
} {
  const now = Date.now()
  const entries = Array.from(domainResolutionCache.entries()).map(
    ([cacheKey, data]) => ({
      cacheKey,
      cacheAge: now - data.cacheTime,
      configVersion: data.configVersion,
    })
  )

  return {
    size: domainResolutionCache.size,
    entries,
  }
}

export default resolveDomainLocale
